// Library methods
import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// MUI Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

// Components
import AddExamSlider from "./AddExamSlider";
import DeleteExamModal from "./DeleteExamModal";
import DuplicateExamModal from "./DuplicateExamModal";
import ExamCard from "./ExamCard";
import LinkDeviceModal from "../../../components/UI/LinkDeviceModal";
import UnavailableDeviceModal from "../../../components/UI/UnavailableDeviceModal";
import PatientInfoSection from "./PatientInfoSection";
import ListHeader from "../../../components/UI/ListHeader";
import PaginationWrapper from "../../../components/UI/PaginationWrapper";
import SnackbarMessage from "../../../components/UI/SnackbarMessage";
import Loader from "../../../components/UI/Loader";
import SharedTabs from "../../../components/UI/SharedTabs";
import ActionableSearchBar from "../../../components/UI/ActionableSearchBar";
import ProgressionAnalysisList from "../ProgressionAnalysisList";
import EditExamSlider from "./EditExamSlider";
import ExamTransferModal from "../../../components/UI/ExamTransferModal";
import ProgressAnalysis from "./ShowProgressAnalysis";
import ExternalExamsListContainer from "../ExternalExamsList";
import { PlusIcon } from "../../../assets/icons";
import { hideLoader, showLoader } from "../../../components/loader/loader";

// Styles
import { ModalCustomCancel } from "../../../styles/muiStylesHelper";

// Utilities
import {
  deleteExam,
  getAllExternalExams,
  getProgressAnalysisStatus,
} from "../../../services/Exam";
import { getComparator } from "../../../utils/tableHelper";
import { itemsRendering, pagesCount } from "../../../utils/paginationHelper";
import {
  getVisualField,
  getAlgorithmType,
  getAllExamSections,
  getTooltipText,
} from "../../../utils/examReportsHelper";
import useClinic from "../../../hooks/useClinic";
import { useDevices } from "../../../hooks/useDevices";
import { useExamStatus } from "../../../hooks/useExamStatus";
import { ToastContext } from "../../../contexts/ToastContext";
import { useProgressionAnalysisList } from "../../../hooks/progression-analysis/useProgressionAnalysisList";
import { eyeTypes } from "../../../enums";
import { useExamListContext } from "../../../contexts/ExamListContext";
import { getPredefinedExams } from "../../../services/Clinic";
import {
  getFilteredExams,
  getValidExams,
  getValidExternalExams,
} from "../../../utils/progressionAnalysisHelper";
import { getTruncatedText } from "../../../utils/textHelper";

const ExamsList = ({
  connection,
  isLoading,
  selectedPatients,
  patients,
  patient,
  page,
  setPage,
}) => {
  // clinic context
  const { clinicId, clinicTimezone, clinicLicensing } = useClinic();

  // internationalization
  const { t } = useTranslation();

  // auth0
  const { getAccessTokenSilently } = useAuth0();

  const {
    rows,
    sectionTypeOrder,
    addModal,
    editModal,
    duplicateModal,
    isSavingExam,
    scheduledDate,
    examCards,
    selectedDuplicateExam,
    availableDevicesOptions,
    linkLoading,
    linkDeviceModal,
    unavailableDeviceModal,
    devicesWithExams,
    editExamDevice,
    editExamId,
    actions,
  } = useExamListContext();
  const {
    handleLinkDevice,
    handleLinkDeviceClick,
    handleLinkDeviceClose,
    handleUnavailableDeviceClose,
    setRows,
    setEditExamDevice,
    refetchExams,
    refetchPatients,
    handleAddOpen,
    handleAddClose,
    handleEditOpen,
    handleEditClose,
    handleDuplicateOpen,
    handleDuplicateClose,
    addExam,
    addDefaultExam,
    duplicateExam,
    editExam,
    onAddExamCallback,
    onEditExamCallback,
    onAddDefaultExamCallback,
    onDuplicateExamCallback,
  } = actions;

  const { unlinkDeviceModal, handleUnlinkDeviceClick } = useDevices(
    connection,
    clinicId,
    patients,
    devicesWithExams
  );

  const { toast, setToast } = useContext(ToastContext);
  const { getExamStatus } = useExamStatus();
  const { eyeLeft, eyeRight } = eyeTypes;

  const patientId = useMemo(() => patient?.id, [patient]);
  const formattedPatientName = useMemo(
    () => getTruncatedText(`${patient?.firstName} ${patient?.lastName}`),
    [patient]
  );

  const [externalExamsList, setExternalExamsList] = useState([]);

  // refetch external exams every time the patient and clinic are changed
  useEffect(() => {
    const fetch = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await getAllExternalExams(token, clinicId, patientId);
        setExternalExamsList(res);
      } catch (e) {}
    };
    fetch();
  }, [getAccessTokenSilently, clinicId, patientId]);

  const hasValidExamsForProgressionAnalysis = useMemo(() => {
    const validExams = getValidExams(rows);
    const validExternalExams = getValidExternalExams(externalExamsList);

    // get valid left eye exams
    const filteredLeftExams = getFilteredExams(
      validExternalExams,
      validExams,
      "Left"
    );

    // get valid right eye exams
    const filteredRightExams = getFilteredExams(
      validExternalExams,
      validExams,
      "Right"
    );

    // to create a progression analysis,
    // patient must have at least 3 valid exams for 1 eye
    return filteredLeftExams.length >= 3 || filteredRightExams.length >= 3;
  }, [externalExamsList, rows]);

  // states init
  const [order, setOrder] = useState("desc");
  const [orderByOptions, setOrderByOptions] = useState(["creationDate"]);
  const [selected, setSelected] = useState([]);
  const [selectedFull, setSelectedFull] = useState([]);
  const [predefinedExams, setPredefinedExams] = useState([]);

  // state for add and delete exams modals
  const [progressAnalysis, setShowProgressAnalysis] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  // state for the search bar
  const [searchTerm, setSearchTerm] = useState("");

  // check if there are more than one patients currently selected
  const isMoreThanOneSelected = selectedPatients.length > 1;

  const maxNumOfRows = 4;
  const examCardRef = useRef(null);

  const [shouldDelete, setShouldDelete] = useState(false);

  const [selectedTabId, setSelectedTabId] = useState(0);

  const [progressionAnalysisFound, setProgressionAnalysisFound] =
    useState(false);

  const displayNoExamsFound = useMemo(
    () => !isLoading && rows.length === 0 && !addModal && selectedTabId === 0,
    [isLoading, rows, addModal, selectedTabId]
  );

  const orderByTypeAlg = ["type", "algorithm", "gridType"];

  // head rows
  const headCells = [
    // This one is displayed before the others, on a different grid.
    {
      id: "device",
      label: t("exams_column_device"),
      gridSize: 1.5,
      orderUsing: [],
    },
    // The below ones are displayed within the same grid, and should sum to 11 (that will be displayed together with a checkbox of grid size 1).
    {
      id: "status",
      label: t("exams_column_status"),
      gridSize: 3,
      orderUsing: ["statusOrder", ...orderByTypeAlg],
    },
    {
      id: "eye",
      label: t("exams_column_eye"),
      gridSize: 2.5,
      orderUsing: ["eyesOrder", ...orderByTypeAlg],
    },
    {
      id: "type",
      label: t("exams_column_type"),
      gridSize: 4.0,
      orderUsing: [...orderByTypeAlg, scheduledDate],
    },
    {
      id: "scheduledDate",
      label: t("exams_column_scheduled_date"),
      gridSize: 2.5,
      orderUsing: ["scheduledDate", ...orderByTypeAlg],
    },
  ];
  const extraCells = [
    {
      id: "manage",
      label: "",
      gridSize: 2,
    },
  ];

  const fetchClinicPackages = useCallback(async () => {
    try {
      // get authToken
      const token = await getAccessTokenSilently();
      const data = await getPredefinedExams(token, clinicId);
      setPredefinedExams([...data]);
    } catch (e) {
      console.log(e);
    }
  }, [clinicId, getAccessTokenSilently]);

  // initial data fetching
  useEffect(() => {
    fetchClinicPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reset selected patients when patients selection changes
  useEffect(() => {
    setSelected([]);
    setSelectedFull([]);
  }, [selectedPatients]);

  useEffect(() => {
    const canDelete = selected.every((examId) => {
      const examData = rows.find((row) => row.id === examId);
      return examData && !examData.associatedDevice;
    });

    setShouldDelete(canDelete);
  }, [rows, selected]);

  // delete exams selected
  const deleteExams = useCallback(async () => {
    // close delete Modal
    setDeleteModal(false);

    // method to generate an exam deletion promise
    const generateDeletePromise = async (examId) => {
      // get token
      const token = await getAccessTokenSilently();
      await deleteExam(token, clinicId, patientId, examId);
    };

    // delete from api
    try {
      const deletePromises = selected.map((examId) =>
        generateDeletePromise(examId)
      );

      await Promise.all(deletePromises);

      refetchPatients();
      setToast(() => ({
        success: true,
        message: t("exams_delete_success"),
      }));
    } catch (error) {
      setToast(() => ({
        success: false,
        message: t("exams_delete_error"),
      }));
    }

    // delete from table
    const newRows = rows.filter((exam) => !selected.includes(exam.id));
    setRows([...newRows]);

    // clear selected
    setSelected([]);
    setSelectedFull([]);
  }, [
    clinicId,
    getAccessTokenSilently,
    patientId,
    rows,
    selected,
    setRows,
    setToast,
    refetchPatients,
    t,
  ]);

  // handle on close of delete exam modal
  const handleDeleteClose = () => {
    setDeleteModal(false);
  };

  // filter change
  const onFilterChange = useCallback(
    (event) => {
      if (page > 1) setPage(1);
      setSearchTerm(event.target.value);
    },
    [page, setPage]
  );

  // clear filter
  const clearSearchFilter = () => {
    setSearchTerm("");
  };

  // clear filter when selected patient changes
  // also reset the current tab back to 0
  useEffect(() => {
    clearSearchFilter();
    setSelectedTabId(0);
  }, [patientId]);

  // pagination change
  const onPaginationChange = (event, value) => {
    setPage(value);
  };

  // handle sort Request (it can now order considering two fields)
  const handleRequestSort = (event, property) => {
    const isAsc =
      orderByOptions.length &&
      orderByOptions[0] === property[0] &&
      order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderByOptions(property);
  };

  // handles select all
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredRows.map((n) => n.id);
      setSelected(newSelecteds);
      setSelectedFull(filteredRows);
      return;
    }
    setSelected([]);
    setSelectedFull([]);
  };

  // handle checkbox clicks
  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleClickToGetFullObject = (event, item) => {
    const selectedIndex = selectedFull.findIndex(
      (selectedItem) => selectedItem.id === item.id
    );
    let newSelectedFull = [];

    if (selectedIndex === -1) {
      // Add the item to the selection
      newSelectedFull = newSelectedFull.concat(selectedFull, item);
    } else if (selectedIndex === 0) {
      // Remove the first item
      newSelectedFull = newSelectedFull.concat(selectedFull.slice(1));
    } else if (selectedIndex === selectedFull.length - 1) {
      // Remove the last item
      newSelectedFull = newSelectedFull.concat(selectedFull.slice(0, -1));
    } else if (selectedIndex > 0) {
      // Remove the item at the selected index
      newSelectedFull = newSelectedFull.concat(
        selectedFull.slice(0, selectedIndex),
        selectedFull.slice(selectedIndex + 1)
      );
    }

    setSelectedFull(newSelectedFull);
  };

  const handleChangeTab = useCallback(
    (newTabId) => setSelectedTabId(newTabId),
    []
  );

  // get completion text, used for filtering
  const getCompletionText = useCallback(
    (section) => {
      let val = "";
      if (section.includes(eyeLeft)) {
        val = val + t("word_left");
      }
      if (section.includes(eyeLeft) && section.includes(eyeRight)) {
        val = val + " / ";
      }
      if (section.includes(eyeRight)) {
        val = val + t("word_right");
      }
      return val;
    },
    [eyeLeft, eyeRight, t]
  );

  // Filtered rows
  const filteredRows = useMemo(
    () =>
      rows
        .filter((row) => getAllExamSections(row)?.length > 0) // filter the exams with no sections
        .filter((row) => {
          const rowSections = getAllExamSections(row);
          const rowGrid = rowSections?.[0]?.gridType ?? "";
          if (
            searchTerm.length &&
            row.type.toLowerCase().indexOf(searchTerm.toLowerCase()) < 0 &&
            row.scheduledDate?.slice(0, 10).indexOf(searchTerm.toLowerCase()) <
              0 &&
            getExamStatus(row, devicesWithExams)
              ?.status?.toLowerCase()
              .indexOf(searchTerm.toLowerCase()) < 0 &&
            getAlgorithmType(row)
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase()) < 0 &&
            getVisualField(rowGrid)
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase()) < 0 &&
            getCompletionText(
              getAllExamSections(row)?.map((section) => section.eye)
            )
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase()) < 0
          )
            return false;
          return true;
        })
        .map((row) => {
          const rowSections = getAllExamSections(row);
          const rowGrid = rowSections?.[0]?.gridType ?? "";
          return {
            ...row,
            // adding some helpful information to the exam
            statusOrder: getExamStatus(row, devicesWithExams)?.status,
            tutorialOrder: row.requiresTutorial ? t("word_yes") : t("word_no"),
            visualFieldOrder: getVisualField(rowGrid).toLowerCase(),
            eyesOrder: getAllExamSections(row)
              .sort((a, b) => {
                if (typeof a.eye === "string" && typeof b.eye === "string") {
                  return a.eye.localeCompare(b.eye);
                }
                // or any other logic to handle non-string cases
                return 0;
              })
              .map((section) => section.eye)
              .join(""),
            deviceName: row.associatedDevice?.device?.alias || "-",
          };
        })
        .sort(getComparator(order, orderByOptions)),
    [
      devicesWithExams,
      getCompletionText,
      getExamStatus,
      order,
      orderByOptions,
      rows,
      searchTerm,
      t,
    ]
  );

  const rowsToRender = useMemo(() => {
    return itemsRendering(filteredRows, page, maxNumOfRows);
  }, [page, filteredRows, maxNumOfRows]);

  const ExamsPageIntro = useMemo(() => {
    const handleDelete = async () => {
      showLoader();
      await handleGetProgressionAnalysisMultiple();
      hideLoader();
      setDeleteModal(true);
    };

    const handleGetProgressionAnalysisMultiple = async () => {
      if (Array.isArray(selectedFull)) {
        if (selectedFull?.length >= 10) {
          setProgressionAnalysisFound(true);
          return; // Stop further API calls if array is too long
        }
        for (const item of selectedFull) {
          const analysisFound = await handleGetProgressionAnalysis(
            item?.patient?.id,
            item?.id
          );

          if (analysisFound) {
            break; // Stop further API calls if analysis is found
          }
        }
      }
    };

    const handleGetProgressionAnalysis = async (patientId, selectedId) => {
      const token = await getAccessTokenSilently();
      const getAnalysisStatus = await getProgressAnalysisStatus(
        token,
        clinicId,
        patientId,
        selectedId
      );

      if (Array.isArray(getAnalysisStatus?.data)) {
        if (getAnalysisStatus?.data?.length === 0) {
          setProgressionAnalysisFound(false);
          return false;
        } else if (getAnalysisStatus?.data?.length > 0) {
          setProgressionAnalysisFound(true);
          return true; // Indicate that analysis was found
        }
      }

      return false; // Default return value if no analysis found
    };

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        gap="8px"
        width="100%"
        overflow="hidden"
      >
        <ActionableSearchBar
          placeholder={`${t("word_search")}...`}
          onSearchChange={onFilterChange}
          onClear={clearSearchFilter}
          showDeleteButton={selected?.length > 0}
          disableDeleteButton={!shouldDelete}
          deleteButtonOnClick={handleDelete}
          showAddButton={true}
          addButtonOnClick={handleAddOpen}
          selectedRows={selected}
          addTooltip={t("exams_add_exam")}
          searchTooltip={t("word_search_exams")}
        />
        {/* Default Exam Buttons */}
        <Box
          display="flex"
          justifyContent="flex-end"
          flexWrap="nowrap"
          gap="8px"
          width="100%"
          overflow="hidden"
          mb={{ xs: "8px", sm: 0 }}
        >
          {predefinedExams
            ?.sort((a, b) => a?.name?.localeCompare(b?.name))
            ?.map((predefinedExam) => (
              <Tooltip
                key={predefinedExam?.id}
                title={
                  <div style={{ whiteSpace: "pre-line" }}>
                    {`${predefinedExam?.name}:\n ${getTooltipText(JSON.parse(predefinedExam?.content), t)}`}
                  </div>
                }
              >
                <Button
                  startIcon={<PlusIcon />}
                  sx={{
                    ...ModalCustomCancel(),
                    minWidth: 0,
                    maxWidth: { xs: "33.3%", sm: "200px" },
                    overflow: "hidden",
                    height: "40px",
                    padding: "8px",
                  }}
                  onClick={() =>
                    addDefaultExam({
                      patientId,
                      onAddDefaultExamCallback,
                      predefinedExam,
                    })
                  }
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    textAlign="left"
                    sx={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Typography lineHeight="16px" fontSize="16px" noWrap>
                      {predefinedExam?.name}
                    </Typography>
                  </Box>
                </Button>
              </Tooltip>
            ))}
        </Box>
      </Box>
    );
  }, [
    predefinedExams,
    handleAddOpen,
    onAddDefaultExamCallback,
    addDefaultExam,
    onFilterChange,
    getAccessTokenSilently,
    clinicId,
    selectedFull,
    selected,
    shouldDelete,
    patientId,
    t,
  ]);

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const {
    progressionAnalysisList: data,
    isLoading: isLoadingProgressionAnalysisList,
  } = useProgressionAnalysisList({
    clinicId,
    patientId,
    clinicTimezone,
  });

  // Patient exam tabs
  const tabs = [
    {
      id: 0,
      label: t("exams_table_title"),
      content: !isLoading ? (
        <Box width="100%">
          {ExamsPageIntro}
          <Grid container>
            <Grid item xs={12}>
              <ListHeader
                containerPaddingX={0}
                headCells={headCells}
                numSelected={selected.length}
                order={order}
                orderBy={orderByOptions?.[0] ?? "creationDate"}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={filteredRows.length}
                checkBoxGrid={1}
                shouldDisplayGrid="sm"
                displayFirtsBeforeCheckbox
                extraCells={extraCells}
              />
              {rowsToRender.length > 0 && (
                <Box mt={1}>
                  {rowsToRender.map((row) => {
                    const isItemSelected = isSelected(row.id);
                    return (
                      <ExamCard
                        key={row.id}
                        row={row}
                        patientId={patientId}
                        isSelected={isItemSelected}
                        headCells={headCells}
                        handleCheckboxClick={handleClick}
                        handleClickToGetFullObject={handleClickToGetFullObject}
                        handleEditClick={handleEditOpen}
                        handleLinkDeviceClick={handleLinkDeviceClick}
                        handleUnlinkDeviceClick={handleUnlinkDeviceClick}
                        handleDuplicateClick={handleDuplicateOpen}
                        associatedExamDevices={devicesWithExams}
                        linkLoading={linkLoading}
                        unlinkLoading={linkLoading}
                        ref={examCardRef}
                      />
                    );
                  })}
                </Box>
              )}
            </Grid>
          </Grid>
          {rows?.length > rowsToRender?.length && (
            <PaginationWrapper
              page={page}
              count={pagesCount(filteredRows, maxNumOfRows)}
              onChange={onPaginationChange}
            />
          )}
        </Box>
      ) : (
        <Loader containerHeight="30vh" />
      ),
      showTab: true,
    },
    {
      id: 1,
      label: t("progression_analysis"),
      content: !isLoading ? (
        <ProgressionAnalysisList
          fetchedAnalysisList={data}
          patientId={patientId}
          progressAnalysisOnClick={() =>
            setShowProgressAnalysis((prev) => !prev)
          }
          maxNumOfRows={maxNumOfRows}
          patientName={formattedPatientName}
          hasValidExams={hasValidExamsForProgressionAnalysis}
        />
      ) : (
        <Loader containerHeight="30vh" />
      ),
      showTab:
        clinicLicensing?.canUseProgressionReports ||
        (data?.length > 0 && !isLoadingProgressionAnalysisList),
    },
    {
      id: 2,
      label: t("external_exams"),
      content: !isLoading ? (
        <ExternalExamsListContainer
          patientId={patientId}
          maxNumOfRows={maxNumOfRows}
          patientName={formattedPatientName}
        />
      ) : (
        <Loader containerHeight="30vh" />
      ),
      showTab: clinicLicensing?.canUseExternalReports ?? false,
    },
  ];

  // if there is no selected patient
  if (!patientId) {
    return (
      <Box mt="24px">
        <Typography
          display="flex"
          height="40px"
          alignItems="center"
          fontSize={{ sm: "16px", lg: "20px" }}
          fontWeight="bold"
        >
          {t("exams_title")}
        </Typography>
        <Box
          my={12}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          gap="20px"
        >
          <Typography fontWeight={600} fontSize="20px">
            {t("no_patient_currently_selected")}
          </Typography>
          <Typography fontSize="20px" maxWidth="400px">
            {t("exam_display_when_selection")}
          </Typography>
        </Box>
      </Box>
    );
  }

  // if there are more than one selected patients
  if (isMoreThanOneSelected) {
    return (
      <Box mt="24px">
        <Typography
          display="flex"
          height="40px"
          alignItems="center"
          fontSize={{ sm: "16px", lg: "20px" }}
          fontWeight="bold"
        >
          {t("exams_title")}
        </Typography>
        <Box
          my={12}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          gap="20px"
        >
          <Typography fontWeight={600} fontSize="20px">
            More than one patient selected.
          </Typography>
          <Typography fontSize="20px" maxWidth="400px">
            The exams are only displayed when there is only one selected
            patient.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Grid container display="flex" flexDirection="column" gap="8px">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          mt="24px"
          width="100%"
        >
          <Typography
            fontSize={{ sm: "16px", lg: "20px" }}
            fontWeight="bold"
            sx={{
              maxWidth: "100%",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {`${t("exams_title")}: ${`${patient?.firstName} ${patient?.lastName}`}`}
          </Typography>
          <PatientInfoSection patient={patient} />
        </Box>
        <SharedTabs
          tabs={tabs}
          selectedTabId={selectedTabId}
          onChange={handleChangeTab}
        />

        {/* Normal tab content to display */}
        <Box width="100%">{tabs?.[selectedTabId]?.content}</Box>

        {/* Patient is selected, but has no exams (the loading of exams should have finished) */}
        {displayNoExamsFound && (
          <Box
            my={8}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            gap="20px"
          >
            <Typography fontWeight={600} fontSize="20px">
              {formattedPatientName} {t("has_no_exams")}
            </Typography>
            <Typography fontSize="20px" maxWidth="270px">
              {t("click_to_add_exam")}
            </Typography>
          </Box>
        )}
      </Grid>

      <AddExamSlider
        isLoading={isSavingExam}
        visible={addModal}
        onClose={handleAddClose}
        onConfirm={() =>
          addExam({
            patientId,
            selectedExamCards: examCards.selected,
            scheduledDate,
            sectionTypeOrder,
            onAddExamCallback,
          })
        }
        patient={patient}
      />

      {/* Delete exam modal */}
      <DeleteExamModal
        open={deleteModal}
        onClose={handleDeleteClose}
        onConfirm={deleteExams}
        onCancel={handleDeleteClose}
        progressionAnalysisFound={progressionAnalysisFound}
        progressionAnalysisFoundCount={selectedFull.length}
      />
      {/* Add progression analysis modal */}
      <ProgressAnalysis
        open={progressAnalysis}
        setOpen={setShowProgressAnalysis}
        clinicId={clinicId}
        selectedPatients={selectedPatients}
        patient={patient}
      />
      {/* Duplicate exam modal */}
      <DuplicateExamModal
        isLoading={isSavingExam}
        open={duplicateModal}
        onClose={handleDuplicateClose}
        onConfirm={() =>
          duplicateExam({
            selectedDuplicateExam,
            patientId,
            onDuplicateExamCallback,
          })
        }
      />

      {/* Edit exam modal */}
      <EditExamSlider
        isLoading={isSavingExam}
        visible={editModal}
        onClose={handleEditClose}
        onConfirm={() =>
          editExam({
            selectedExamCards: examCards.selected,
            sectionTypeOrder,
            scheduledDate,
            patientId,
            editExamId,
            onEditExamCallback,
          })
        }
        patient={patient}
      />

      {/* Unlink device exam modal */}
      {unlinkDeviceModal}

      {/* Link device modal */}
      <LinkDeviceModal
        open={linkDeviceModal}
        onClose={handleLinkDeviceClose}
        onConfirm={handleLinkDevice}
        onCancel={handleLinkDeviceClose}
        devicesOptions={availableDevicesOptions}
        editExamDevice={editExamDevice}
        setEditExamDevice={setEditExamDevice}
        loading={linkLoading}
      />

      {/* Unavailable device modal */}
      <UnavailableDeviceModal
        open={unavailableDeviceModal}
        onClose={handleUnavailableDeviceClose}
        onCancel={handleUnavailableDeviceClose}
      />

      <ExamTransferModal
        currentPatient={patient}
        patientsOptions={patients}
        afterTransfer={refetchExams}
      />

      {/* Add exam success/failure toasts */}
      <SnackbarMessage
        open={Boolean(toast.message)}
        onClose={() => setToast({ success: false, message: null })}
        success={toast.success}
        autoHideDuration={8000}
        text={toast.message}
      />
    </>
  );
};

export default ExamsList;
